import { AlertTriangle, RefreshCw, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { MediaThumb } from "../components/MediaThumb";
import { ApiError, api } from "../lib/api";
import type { HistoryItem } from "../lib/types";

type ConfirmAction =
  | {
      type: "single";
      item: HistoryItem;
    }
  | {
      type: "all";
    };

function ConfirmModal({
  action,
  onCancel,
  onConfirm
}: {
  action: ConfirmAction;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isClearAll = action.type === "all";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-md rounded border border-line bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-line p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded bg-red-50 p-2 text-red-600">
              <AlertTriangle size={18} />
            </span>
            <div>
              <h2 className="text-base font-semibold text-ink">{isClearAll ? "Clear all history?" : "Delete history item?"}</h2>
              <p className="mt-1 text-sm leading-5 text-slate-500">
                {isClearAll
                  ? "ลบประวัติทั้งหมดในระบบเท่านั้น ข้อความที่ส่งไปแล้วใน Telegram จะไม่ถูกลบ"
                  : "ลบรายการนี้ออกจากประวัติในระบบเท่านั้น ข้อความที่ส่งไปแล้วใน Telegram จะไม่ถูกลบ"}
              </p>
            </div>
          </div>
          <button type="button" onClick={onCancel} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close confirmation">
            <X size={18} />
          </button>
        </div>
        <div className="flex justify-end gap-2 p-4">
          <button type="button" onClick={onCancel} className="rounded border border-line bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="inline-flex items-center gap-2 rounded bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700">
            <Trash2 size={16} />
            {isClearAll ? "Clear All History" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function HistoryPage() {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const result = await api.history();
      setItems(result.items);
      setError("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "โหลด history ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const confirmDelete = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.type === "all") {
        await api.clearHistory();
        setNotice("ล้าง History ทั้งหมดในระบบแล้ว");
      } else {
        await api.deleteHistory(confirmAction.item.id);
        setNotice("ลบ History รายการนี้แล้ว");
      }
      setError("");
      setConfirmAction(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ลบ history ไม่สำเร็จ");
      setConfirmAction(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">History</h1>
          <p className="mt-1 text-sm text-slate-500">ประวัติการส่ง Telegram และผลลัพธ์จาก API</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setConfirmAction({ type: "all" })}
            disabled={items.length === 0}
            className="inline-flex items-center gap-2 rounded border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 size={16} />
            Clear All History
          </button>
        </div>
      </div>

      {notice ? <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div> : null}
      {error ? <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

      <div className="overflow-hidden rounded border border-line bg-white shadow-sm">
        <table className="w-full min-w-[1020px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Sender</th>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Media / Preview</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Message ID</th>
              <th className="px-4 py-3">Error</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-line">
                <td className="px-4 py-3">{new Date(item.createdAt).toLocaleString("th-TH")}</td>
                <td className="px-4 py-3">{item.createdByName ?? item.createdBy}</td>
                <td className="px-4 py-3">{item.targetName ?? item.channelName ?? item.targetTelegramId ?? item.channelTelegramId ?? item.channelId}</td>
                <td className="px-4 py-3">{(item.targetType ?? "channel") === "group" ? "Group" : "Channel"}</td>
                <td className="px-4 py-3"><MediaThumb preview={item.mediaPreview} mediaType={item.mediaType} /></td>
                <td className="max-w-[220px] truncate px-4 py-3">{item.title || "-"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded px-2 py-1 text-xs font-semibold ${item.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                    {item.status}
                  </span>
                </td>
                <td className="px-4 py-3">{item.telegramMessageId ?? "-"}</td>
                <td className="max-w-[260px] truncate px-4 py-3 text-red-600">{item.error ?? "-"}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => setConfirmAction({ type: "single", item })}
                    className="inline-flex items-center gap-1 rounded border border-red-200 px-2.5 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={15} />
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 ? <div className="p-6 text-sm text-slate-500">ยังไม่มีประวัติการส่ง</div> : null}
      </div>

      {confirmAction ? <ConfirmModal action={confirmAction} onCancel={() => setConfirmAction(null)} onConfirm={confirmDelete} /> : null}
    </div>
  );
}
